import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { API_BASE } from "../DataContext";
import { ImagePickerModal } from "./ImagePickerModal";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", coverImage: "", author: "BAISHALII Team", tags: "", published: true };

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

export const AdminBlog = ({ token, onUnauthorized }) => {
  const { toast } = useToast();
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/admin/blog`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) return onUnauthorized();
    setPosts(await res.json());
  }, [token, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ ...EMPTY, id: null });
  const openEdit = (p) => setEditing({ ...p, tags: p.tags.join(", ") });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = JSON.stringify({
      title: editing.title,
      slug: editing.slug,
      excerpt: editing.excerpt,
      content: editing.content,
      coverImage: editing.coverImage,
      author: editing.author,
      tags: editing.tags.split(",").map((t) => t.trim()).filter(Boolean),
      published: editing.published
    });
    try {
      const res = editing.id
        ? await fetch(`${API_BASE}/api/admin/blog/${editing.id}`, { method: "PUT", headers, body })
        : await fetch(`${API_BASE}/api/admin/blog`, { method: "POST", headers, body });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Save failed");
      toast({ title: editing.id ? "Post updated" : "Post published", description: data.title });
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    const res = await fetch(`${API_BASE}/api/admin/blog/${p.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      toast({ title: "Post deleted", description: p.title });
      load();
    } else {
      toast({ title: "Delete failed", description: "Please try again.", variant: "destructive" });
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8 space-y-4" data-testid="admin-blog-editor">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black text-[#0a2a5e]">{editing.id ? "Edit Post" : "Write New Post"}</h2>
          <button type="button" onClick={() => setEditing(null)} data-testid="blog-editor-close" className="p-2 rounded-full hover:bg-slate-100"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Title *</label>
            <Input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="blog-title-input" placeholder="Post title" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Slug (leave blank to auto-generate)</label>
            <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="blog-slug-input" placeholder="my-post-url" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Excerpt (shown on the blog cards)</label>
          <Textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} data-testid="blog-excerpt-input" placeholder="One or two lines summarising the article..." />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Cover Image URL</label>
            <div className="flex gap-2">
              <Input value={editing.coverImage} onChange={(e) => setEditing({ ...editing, coverImage: e.target.value })} data-testid="blog-cover-input" placeholder="/blog/my-image.webp or https://..." className="flex-1" />
              <Button type="button" onClick={() => setPickerOpen(true)} data-testid="blog-cover-pick-btn"
                className="rounded-lg bg-sky-500 hover:bg-sky-600 text-white h-10 px-3 shrink-0" title="Pick from Image Manager">
                <ImagePlus className="h-4 w-4" />
              </Button>
            </div>
            {editing.coverImage && (
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                <img src={editing.coverImage} alt="cover preview" className="h-10 w-16 object-cover rounded border border-slate-200" />
                <button type="button" onClick={() => setEditing({ ...editing, coverImage: "" })} className="text-red-500 hover:underline">Clear</button>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Author</label>
            <Input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} data-testid="blog-author-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Tags (comma separated)</label>
            <Input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} data-testid="blog-tags-input" placeholder="Plumbing, Buying Guide" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Content * (Markdown supported: ## headings, **bold**, - lists, tables)</label>
          <Textarea required rows={16} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid="blog-content-input" className="font-mono text-sm" placeholder={"## Section heading\n\nYour paragraph text...\n\n- bullet one\n- bullet two"} />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
            <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="blog-published-checkbox" className="h-4 w-4 accent-[#0a2a5e]" />
            Published (visible on the website)
          </label>
          <Button type="submit" disabled={saving} data-testid="blog-save-btn" className="bg-gradient-to-r from-sky-500 to-[#0a2a5e] hover:opacity-90 text-white rounded-full px-8">
            {saving ? "Saving..." : editing.id ? "Update Post" : "Publish Post"}
          </Button>
        </div>
        {pickerOpen && (
          <ImagePickerModal
            token={token}
            onSelect={(url) => setEditing((prev) => ({ ...prev, coverImage: url }))}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </form>
    );
  }

  return (
    <div data-testid="admin-blog-list">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="blog-new-post-btn" className="bg-gradient-to-r from-sky-500 to-[#0a2a5e] hover:opacity-90 text-white rounded-full px-5">
          <Plus className="h-4 w-4 mr-1.5" /> Write New Post
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-5 py-3.5 font-semibold">Date</th>
              <th className="px-5 py-3.5 font-semibold">Title</th>
              <th className="px-5 py-3.5 font-semibold">Tags</th>
              <th className="px-5 py-3.5 font-semibold">Status</th>
              <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!posts && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Loading...</td></tr>}
            {posts && posts.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No posts yet. Write your first one!</td></tr>}
            {posts && posts.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-5 py-3.5 whitespace-nowrap text-slate-500">{fmtDate(p.created_at)}</td>
                <td className="px-5 py-3.5 font-semibold text-[#0a2a5e]">{p.title}</td>
                <td className="px-5 py-3.5 text-slate-500">{p.tags.join(", ")}</td>
                <td className="px-5 py-3.5">
                  <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${p.published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {p.published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(p)} data-testid={`blog-edit-${p.slug}`} className="p-2 rounded-full hover:bg-sky-100 text-sky-700" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p)} data-testid={`blog-delete-${p.slug}`} className="p-2 rounded-full hover:bg-red-50 text-red-600 ml-1" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
